import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {CoreExchangeHeader} from '@dms/api/warehouse';
import {customerRoutes, vehiclesRoutes, warehouseRoutes, workshopRoutes} from '@dms/routes';
import {getCustomerName, queryParams} from '@dms/shared';

import {buildArray, composePath, Nullish} from 'shared/web';

import {openInNewTab} from '../../../utils/openInNewTab';

const getOriginEntityPath = (header: CoreExchangeHeader) =>
  match(header.originEntityType)
    .with('direct-sale', () =>
      composePath(warehouseRoutes.directSalesDetailOverview, {
        params: {id: header.originEntityId},
        isSearchParamsPreserved: false,
      })
    )
    .with('service-case-order', () =>
      isNil(header.originEntityHeaderId)
        ? null
        : composePath(workshopRoutes.serviceCaseDetail, {
            params: {id: header.originEntityHeaderId},
            queryParams: {[queryParams.SERVICE_CASE_ORDER_ID]: header.originEntityId},
            isSearchParamsPreserved: false,
          })
    )
    .otherwise(() => null);

export function useCoreExchangeHeaderLinks(header: CoreExchangeHeader | Nullish) {
  const customerId = header?.customerId;
  const vehicleId = header?.vehicleId;

  const {data: customer} = useGetCustomerV2Query(isNil(customerId) ? skipToken : {customerId});
  const {data: vehicle} = useGetVehicleQuery(isNil(vehicleId) ? skipToken : {vehicleId});

  const customerName = getCustomerName(customer);
  const vehicleTitle = vehicle?.title;
  const originEntityNumber = header?.originEntityNumber;
  const originEntityPath = isNil(header) ? null : getOriginEntityPath(header);

  return buildArray<Parameter>()
    .when(isNotNil(customerId) && isNotNil(customerName), {
      title: customerName,
      icon: 'social/person',
      variant: 'link',
      onClick: () => openInNewTab(composePath(customerRoutes.detail, {params: {id: customerId}})),
    })
    .when(isNotNil(vehicleId) && isNotNil(vehicleTitle), {
      title: vehicleTitle,
      icon: 'maps/directions_car',
      variant: 'link',
      onClick: () => openInNewTab(composePath(vehiclesRoutes.detail, {params: {id: vehicleId}})),
    })
    .when(isNotNil(originEntityNumber), {
      title: originEntityNumber,
      icon: 'action/build',
      variant: 'link',
      onClick: isNil(originEntityPath) ? undefined : () => openInNewTab(originEntityPath),
    });
}
